import React, { useState, useEffect, useMemo } from "react";

// @mui material components
import Divider from "@mui/material/Divider";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";

// Custom styles for the Modal
import ModalRoot from "containers/modal/ModalRoot";
import { Checkbox, FormControlLabel, Icon } from "@mui/material";
import { toast } from "react-toastify";
import Createable from "containers/Select";
import Grid from "@mui/material/Grid";

import {
  calcPriceWT,
  formatAmount,
  getServiceTotalAmountIncludingGst,
  isUnregisteredGstTreatment,
  normalizeCurrencyInput,
  parseStrictNumber,
} from "utils/common";
import CustomButton from "containers/CustomButton";
import { EditPayment } from "services/clientdetails";
import { AddPayment } from "services/clientdetails";
import ModalHeader from "containers/modal-header";
import { calculateGstBalance } from "utils/common";
import GST from "components/GST/GST";
import DatePicker from "react-datepicker";

function PaymentModal({
  open,
  setSelectedClient,
  handleCloseModal,
  id,
  selectedItem,
  setSelectedItem,
  services,
  mode,
  setMode,
  employeeArray,
  clientBalanceReceivableDate,
}) {
  const getDatePickerValue = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (
      typeof value === "object" &&
      value.seconds !== undefined &&
      typeof value.seconds === "number"
    ) {
      const fromTimestamp = new Date(value.seconds * 1000);
      return Number.isNaN(fromTimestamp.getTime()) ? null : fromTimestamp;
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  //for service options list
  const [serviceOptions, setServiceOptions] = useState([]);
  useEffect(() => {
    let serviceOptionsArray = services.map((item) => {
      const gstType = item?.gstType || {
        label: "CGST & SGST",
        value: "CGST & SGST",
      };
      const gst =
        item?.gst ??
        (gstType?.value === "IGST"
          ? item?.igst ?? 0
          : (item?.sgst ?? 0) + (item?.cgst ?? 0));
      const numericGst = parseFloat(gst) || 0;
      const hasSplitGst =
        (parseFloat(item?.sgst) || 0) > 0 || (parseFloat(item?.cgst) || 0) > 0;
      const sgst =
        gstType?.value === "IGST"
          ? 0
          : hasSplitGst
          ? item?.sgst ?? 0
          : numericGst / 2;
      const cgst =
        gstType?.value === "IGST"
          ? 0
          : hasSplitGst
          ? item?.cgst ?? 0
          : numericGst / 2;
      const igst =
        gstType?.value === "IGST" ? item?.igst || numericGst : item?.igst ?? 0;

      return {
        label: item.serviceName,
        value: item.id,
        totalAmount: item.sellingPriceWOT,
        totalAmountWithTax: calcPriceWT(item.sellingPriceWOT, gst),
        hsnCode: item?.hsnCode ? item?.hsnCode : "N/A",
        checkGst: item?.checkGst || numericGst > 0,
        gstType,
        gst,
        igst,
        sgst,
        cgst,
        key: item.id,
      };
    });
    setServiceOptions(serviceOptionsArray);
  }, [services]);

  //default modal fields value
  const defaultHandler = useMemo(() => {
    return {
      startDate: null,
      completionDate: null,
      service: null,
      discount: "",
      amountPaid: "",
      totalAmount: null,
      totalAmountWithTax: null,
      clientGst: 0,
      serviceInstance: null,
      paymentMode: null,
      complementaryService: null,
      notes: null,
      assignDietitian: null,
      balanceReceivableDate: clientBalanceReceivableDate ?? null,
      checkGst: null,
      gstTreatment: null,
      gstType: { label: "CGST & SGST", value: "CGST & SGST" },
      gst: "",
      igst: null,
      sgst: "",
      cgst: "",
      gstComment: null,
      gstNumber: null,
    };
  }, [clientBalanceReceivableDate]);

  //error handler
  const errorDefault = useMemo(() => {
    return {
      startDate: false,
      completionDate: false,
      service: false,
      discount: false,
      amountPaid: false,
      serviceInstance: false,
      paymentMode: false,
      complementaryService: false,
      notes: false,
      assignDietitian: false,
      gstTreatment: false,
      gstType: false,
      igst: false,
      sgst: false,
      cgst: false,
    };
  }, []);

  const [formdata, setFormdata] = useState(defaultHandler);

  const [errorMessage, setErrorMessage] = useState(errorDefault);

  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (selectedItem) {
      // Prefer tax-exclusive base. After invoice save, totalAmount used to be
      // overwritten with the inclusive total; totalAmountBeforeTax / packageAmount
      // still hold the correct WOT value for those records.
      const exclusiveTotalAmount =
        selectedItem.totalAmountBeforeTax ??
        selectedItem.packageAmount ??
        selectedItem.totalAmount;

      setFormdata({
        ...defaultHandler,
        ...selectedItem,
        totalAmount: exclusiveTotalAmount,
        serviceInstance: {
          value: selectedItem?.serviceInstance,
          label: selectedItem?.serviceInstance,
        },
        paymentMode: {
          value: selectedItem?.paymentMode,
          label: selectedItem?.paymentMode,
        },
        assignDietitian: selectedItem?.assignDietitian ?? null,
        balanceReceivableDate:
          selectedItem?.balanceReceivableDate ??
          clientBalanceReceivableDate ??
          null,
        gstNumber: selectedItem?.gstNumber || null,
        service: {
          value: selectedItem.serviceId,
          label: selectedItem.serviceName,
          totalAmount: exclusiveTotalAmount,
          totalAmountWithTax: selectedItem.totalAmountWithTax,
        },
      });
    }
  }, [selectedItem, defaultHandler, clientBalanceReceivableDate]);

  //for submitting data
  const submitHandler = async (e) => {
    if (!formdata.startDate) {
      toast.error("Please fill Start Date field");
      setErrorMessage({
        ...errorMessage,
        startDate: true,
      });
      return;
    }

    if (!formdata.completionDate) {
      toast.error("Please fill Completion Date field");
      setErrorMessage({
        ...errorMessage,
        completionDate: true,
      });
      return;
    }

    if (new Date(formdata.startDate) > new Date(formdata.completionDate)) {
      toast.info("Completion Date should be greater than Start Date");
      setErrorMessage({
        ...errorMessage,
        completionDate: true,
      });
      return;
    }

    if (!formdata.service) {
      toast.error("Please select Choose Package field");
      setErrorMessage({
        ...errorMessage,
        service: true,
      });
      return;
    }

    if (formdata?.checkGst) {
      const gstNumberRegex =
        /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/;
      const gstNumberMatch = formdata?.gstNumber?.match(gstNumberRegex);
      const isUnregistered = isUnregisteredGstTreatment(formdata?.gstTreatment);
      if (!formdata.gstTreatment) {
        toast.error("Please fill GST treatment field");
        setErrorMessage({
          ...errorMessage,
          gstTreatment: true,
        });
        return;
      }
      if (!isUnregistered) {
        if (
          !formdata?.gstNumber?.length ||
          formdata?.gstNumber === undefined ||
          formdata?.gstNumber === null
        ) {
          toast.error("Please fill GST number!!");
          return;
        } else if (!gstNumberMatch) {
          toast.error("Invalid GST number!!");
          return;
        }

        if (formdata?.gstType.value === "IGST") {
          if (!formdata.igst) {
            toast.error("Please fill IGST field");
            setErrorMessage({
              ...errorMessage,
              igst: true,
            });
            return;
          }
        } else {
          if (!formdata.sgst) {
            toast.error("Please fill SGST field");
            setErrorMessage({
              ...errorMessage,
              sgst: true,
            });
            return;
          }

          if (!formdata.cgst) {
            toast.error("Please fill CGST field");
            setErrorMessage({
              ...errorMessage,
              cgst: true,
            });
            return;
          }
        }
      }
    }

    if (formdata.amountPaid === null || formdata.amountPaid === "") {
      toast.error("Please fill Amount Paid field");
      setErrorMessage({
        ...errorMessage,
        amountPaid: true,
      });
      return;
    }

    if (!formdata.serviceInstance) {
      toast.error("Please select Service Instance field");
      setErrorMessage({
        ...errorMessage,
        serviceInstance: true,
      });
      return;
    }

    if (!formdata.paymentMode) {
      toast.error("Please select Payment Mode field");
      setErrorMessage({
        ...errorMessage,
        paymentMode: true,
      });
      return;
    }

    setLoader(true);
    try {
      const normalizedFormdata = {
        ...formdata,
        discount: parseStrictNumber(formdata.discount),
      };
      let data;
      if (selectedItem) {
        data = await EditPayment({ formdata: normalizedFormdata, id: id });
        setSelectedClient((e) => {
          e = {
            ...e,
            services: data,
            balanceReceivableDate:
              normalizedFormdata.balanceReceivableDate ??
              e.balanceReceivableDate ??
              null,
          };
          return e;
        });
        toast.success("Successfully updated client");
      } else {
        data = await AddPayment({ formdata: normalizedFormdata, id: id });
        setSelectedClient((e) => {
          e = {
            ...e,
            services: e.services ? [data, ...e.services] : [data],
            balanceReceivableDate:
              normalizedFormdata.balanceReceivableDate ??
              e.balanceReceivableDate ??
              null,
          };
          return e;
        });
        toast.success("Successfully added service");
      }

      handleCloseModal();

      setTimeout(() => {
        setSelectedItem(null);
        setLoader(false);
        setFormdata({
          ...defaultHandler,
        });
        setErrorMessage({
          ...errorDefault,
        });
      }, 500);
    } catch (e) {
      setLoader(false);
      toast.error(e?.message ?? "Something went wrong retry");
    }
  };

  const [serviceInstanceOptions] = useState([
    { value: "First Time", label: "First Time", key: 1 },
    { value: "Extension", label: "Extension", key: 2 },
    { value: "Returning", label: "Returning", key: 3 },
  ]);

  const [paymentOptions] = useState([
    { value: "Cash", label: "Cash", key: 1 },
    { value: "Online", label: "Online", key: 2 },
    { value: "UPI", label: "UPI", key: 3 },
    { value: "Card", label: "Card", key: 4 },
    { value: "Net Banking", label: "Net Banking", key: 5 },
  ]);

  const calculateBalance = () => {
    if (formdata.totalAmount) {
      return (
        parseStrictNumber(formdata.totalAmount) -
        parseStrictNumber(formdata.discount) -
        parseStrictNumber(formdata.amountPaid)
      ).toFixed(2);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    if (name === "discount" || name === "amountPaid") {
      updatedValue = normalizeCurrencyInput(value);
      if (updatedValue === null) return;
    }
    if (name === "discount" && updatedValue === "") {
      setErrorMessage((prev) => ({
        ...prev,
        discount: false,
      }));
    }
    if (name === "discount") {
      const discountValue = parseStrictNumber(updatedValue);
      const totalAmount = parseStrictNumber(formdata?.totalAmountWithTax);
      const amountPaid = parseStrictNumber(formdata?.amountPaid);
      const balance = totalAmount - amountPaid;
      const roundedDiscountValue = Math.round(discountValue * 100) / 100;
      const roundedTotalAmount = Math.round(totalAmount * 100) / 100;
      const roundedBalance = Math.round(balance * 100) / 100;
      if (
        roundedDiscountValue > roundedTotalAmount ||
        roundedDiscountValue > roundedBalance
      ) {
        toast("Can't exceed balance amount!");
        return;
      }
    }
    if (name === "amountPaid") {
      const amountPaid = parseStrictNumber(updatedValue);
      const discountValue = parseStrictNumber(formdata?.discount);
      const totalAmount = parseStrictNumber(formdata?.totalAmountWithTax);
      const balance = totalAmount - discountValue;
      const roundedAmountPaid = Math.round(amountPaid * 100) / 100;
      const roundedTotalAmount = Math.round(totalAmount * 100) / 100;
      const roundedBalance = Math.round(balance * 100) / 100;
      if (
        roundedAmountPaid > roundedTotalAmount ||
        roundedAmountPaid > roundedBalance
      ) {
        toast("Can't exceed balance amount!");
        return;
      }
    }
    setFormdata((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));
  };

  useEffect(() => {
    if (formdata?.checkGst) {
      const fallbackGst =
        parseStrictNumber(formdata?.clientGst) ||
        parseStrictNumber(formdata?.gst);
      const gst =
        formdata.gstType?.value === "IGST"
          ? parseStrictNumber(formdata?.igst) || fallbackGst
          : parseStrictNumber(formdata?.sgst) +
              parseStrictNumber(formdata?.cgst) ||
            fallbackGst;

      setFormdata((prevData) => ({
        ...prevData,
        gst,
        clientGst: gst,
      }));
    } else {
      setFormdata((prevData) => ({
        ...prevData,
        gst: 0,
        clientGst: 0,
      }));
    }
  }, [
    formdata?.gstType?.value,
    formdata?.checkGst,
    formdata?.igst,
    formdata?.sgst,
    formdata?.cgst,
    formdata?.clientGst,
    formdata?.gst,
  ]);

  useEffect(() => {
    // View mode should show persisted totals; recalculating here re-applies GST
    // on amounts that may already be tax-inclusive after invoice generation.
    if (mode === "view") return;

    const newBalance = calculateGstBalance(
      formdata?.service?.totalAmount,
      formdata?.clientGst,
      formdata?.discount,
      formdata?.amountPaid
    );
    setFormdata((prevData) => ({
      ...prevData,
      balance: newBalance.balance,
      totalAmountWithTax: newBalance.totalAmountWithTax,
      totalAmount:
        parseFloat(formdata?.service?.totalAmount) || prevData.totalAmount,
    }));
  }, [
    mode,
    formdata?.service?.totalAmount,
    formdata?.clientGst,
    formdata?.discount,
    formdata?.amountPaid,
  ]);

  return (
    <ModalRoot
      variant="permanent"
      ownerState={{ openConfigurator: open }}
      width="75%"
    >
      <SuiBox
        style={{ whiteSpace: "normal !important" }}
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
      >
        <ModalHeader mode={mode} title="Service, Payment & Balance History" />

        <Grid item>
          {mode === "view" && (
            <Icon
              sx={({
                typography: { size, fontWeightBold },
                palette: { dark },
              }) => ({
                fontSize: `${size.md} !important`,
                fontWeight: `${fontWeightBold} !important`,
                stroke: dark.main,
                strokeWidth: "2px",
                cursor: "pointer",
                mt: 2,
                mr: 2,
              })}
              onClick={() => {
                setMode("edit");
              }}
            >
              edit
            </Icon>
          )}
          {mode === "edit" && (
            <Icon
              sx={({
                typography: { size, fontWeightBold },
                palette: { dark },
              }) => ({
                fontSize: `${size.md} !important`,
                fontWeight: `${fontWeightBold} !important`,
                stroke: dark.main,
                strokeWidth: "2px",
                cursor: "pointer",
                mt: 2,
                mr: 2,
              })}
              onClick={() => {
                setMode("view");
              }}
            >
              visibility
            </Icon>
          )}
          <Icon
            sx={({
              typography: { size, fontWeightBold },
              palette: { dark },
            }) => ({
              fontSize: `${size.md} !important`,
              fontWeight: `${fontWeightBold} !important`,
              stroke: dark.main,
              strokeWidth: "2px",
              cursor: "pointer",
              mt: 2,
            })}
            onClick={() => {
              handleCloseModal();
              setTimeout(() => {
                setFormdata({
                  ...defaultHandler,
                });
                setErrorMessage({
                  ...errorDefault,
                });
              }, 500);
            }}
          >
            close
          </Icon>
        </Grid>
      </SuiBox>

      <Divider />

      <SuiBox pt={1.25} pb={3} px={3}>
        <SuiBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Start Date
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="date"
                  name="startDate"
                  readOnly={mode === "view"}
                  error={errorMessage?.startDate}
                  value={formdata.startDate ?? ""}
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      [e.target.name]: false,
                    });
                    setFormdata({
                      ...formdata,
                      [e.target.name]: e.target.value,
                    });
                  }}
                />
              </SuiBox>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Completion Date
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="date"
                  name="completionDate"
                  readOnly={mode === "view"}
                  error={errorMessage?.completionDate}
                  value={formdata.completionDate ?? ""}
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      [e.target.name]: false,
                    });
                    setFormdata({
                      ...formdata,
                      [e.target.name]: e.target.value,
                    });
                  }}
                />
              </SuiBox>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Choose Package
                </SuiTypography>
                <Createable
                  options={serviceOptions}
                  disabled={mode === "view"}
                  style={{ borderRadius: "0.5rem", fontSize: 14 }}
                  inputStyle={{
                    width: 190,
                    height: "1.850rem",
                    borderRadius: "0.5rem",
                  }}
                  onChange={(e) => {
                    setFormdata({
                      ...formdata,
                      service: e,
                      totalAmount: e.totalAmount,
                      totalAmountWithTax: e.totalAmountWithTax,
                      packageAmount: e.totalAmount,
                      totalAmountBeforeTax: e.totalAmount,
                      hsnCode: e.hsnCode,
                      checkGst: e.checkGst,
                      gstType: e.gstType,
                      gst: e.gst,
                      igst: e.igst,
                      sgst: e.sgst,
                      cgst: e.cgst,
                      gstTreatment: null,
                      gstComment: null,
                      gstNumber: null,
                    });
                  }}
                  value={formdata.service}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        <SuiBox mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <SuiBox>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={mode === "view"}
                      checked={!!formdata?.checkGst}
                      onChange={() => {
                        const nextCheckGst = !formdata?.checkGst;
                        setFormdata({
                          ...formdata,
                          checkGst: nextCheckGst,
                          gstTreatment: null,
                          igst: "",
                          sgst: "",
                          cgst: "",
                          gstNumber: null,
                        });
                      }}
                    />
                  }
                  label="GST"
                  sx={{ marginLeft: 0, width: "fit-content" }}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        {formdata?.checkGst && (
          <SuiBox mt={3}>
            <GST
              formdata={formdata}
              setFormdata={setFormdata}
              setErrorMessage={setErrorMessage}
              errorMessage={errorMessage}
              mode={mode}
              gstTreatment
            />
          </SuiBox>
        )}

        <SuiBox mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Total Amount
                </SuiTypography>
                <SuiTypography mb={1} variant="h7">
                  INR{" "}
                  {formdata?.service
                    ? formatAmount(getServiceTotalAmountIncludingGst(formdata))
                    : "-"}
                </SuiTypography>
              </SuiBox>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Discount (In INR)
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="text"
                  name="discount"
                  readOnly={mode === "view"}
                  error={errorMessage?.discount}
                  value={formdata.discount ?? ""}
                  onChange={handleInputChange}
                />
              </SuiBox>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Amount Paid
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="number"
                  name="amountPaid"
                  readOnly={mode === "view"}
                  error={errorMessage?.amountPaid}
                  value={formdata.amountPaid ?? ""}
                  onChange={handleInputChange}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        <SuiBox mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <SuiTypography mb={1} variant="h6">
                Balance
              </SuiTypography>
              <SuiTypography mb={1} variant="h7" style={{ color: "red" }}>
                INR{" "}
                {!isNaN(formdata?.balance)
                  ? parseFloat(formdata?.balance).toFixed(2)
                  : calculateBalance()}
              </SuiTypography>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Assign Dietitian
                </SuiTypography>
                <Createable
                  value={formdata?.assignDietitian}
                  options={employeeArray ?? []}
                  disabled={mode === "view"}
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={true}
                  style={{
                    borderRadius: "0.5rem",
                    fontSize: 14,
                    zIndex: 999,
                  }}
                  inputStyle={{
                    width: 190,
                    height: "1.800rem",
                    borderRadius: "0.5rem",
                  }}
                  placeholder="Choose Employee Access"
                  onChange={(options) => {
                    if (Array.isArray(options)) {
                      setFormdata({
                        ...formdata,
                        assignDietitian: options.map((opt) => ({
                          value: opt.value,
                          label: opt.label,
                        })),
                      });
                    } else {
                      setFormdata({
                        ...formdata,
                        assignDietitian: null,
                      });
                    }
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Balance Receivable Date
                </SuiTypography>
                <DatePicker
                  selected={getDatePickerValue(formdata?.balanceReceivableDate)}
                  onChange={(date) => {
                    setFormdata({
                      ...formdata,
                      balanceReceivableDate: date ?? null,
                    });
                  }}
                  disabled={mode === "view"}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Optional"
                  className="date-picker-custom-v1"
                  isClearable={mode !== "view"}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Service Instance
                </SuiTypography>
                <Createable
                  disabled={mode === "view"}
                  options={serviceInstanceOptions}
                  style={{ borderRadius: "0.5rem", fontSize: 14 }}
                  inputStyle={{
                    width: 190,
                    height: "1.850rem",
                    borderRadius: "0.5rem",
                  }}
                  onChange={(e) => {
                    setFormdata({
                      ...formdata,
                      serviceInstance: e,
                    });
                  }}
                  value={formdata.serviceInstance}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Payment Mode
                </SuiTypography>
                <Createable
                  disabled={mode === "view"}
                  options={paymentOptions}
                  style={{ borderRadius: "0.5rem", fontSize: 14 }}
                  inputStyle={{
                    width: 190,
                    height: "1.850rem",
                    borderRadius: "0.5rem",
                  }}
                  onChange={(e) => {
                    setFormdata({
                      ...formdata,
                      paymentMode: e,
                    });
                  }}
                  value={formdata.paymentMode}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        <SuiBox mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <SuiBox>
                <SuiTypography mb={1} variant="h6">
                  Complementary Service?
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="text"
                  readOnly={mode === "view"}
                  name="complementaryService"
                  placeholder="Some other service ..."
                  rows={6}
                  multiline
                  value={formdata.complementaryService ?? ""}
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      [e.target.name]: false,
                    });
                    setFormdata({
                      ...formdata,
                      [e.target.name]: e.target.value,
                    });
                  }}
                />
              </SuiBox>
            </Grid>
            <Grid item xs={12} lg={6}>
              <SuiBox>
                <SuiTypography mb={1} variant="h6">
                  Notes/Guidelines
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="text"
                  readOnly={mode === "view"}
                  name="notes"
                  rows={6}
                  multiline
                  value={formdata.notes ?? ""}
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      [e.target.name]: false,
                    });
                    setFormdata({
                      ...formdata,
                      [e.target.name]: e.target.value,
                    });
                  }}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        {mode !== "view" && (
          <SuiBox mt={3} mb={2} display="flex" justifyContent="center">
            <SuiBox mb={2} width="32%">
              <CustomButton
                color="dark"
                variant="gradient"
                onClick={submitHandler}
                fullWidth
                width={180}
                disabled={loader || !open}
                title={selectedItem ? "Edit Data" : "Add Data"}
                loader={loader}
              />
            </SuiBox>
          </SuiBox>
        )}
      </SuiBox>
    </ModalRoot>
  );
}

export default PaymentModal;
